// Record values iOS keeps local-only and recomputes from synced activities (WEB_PLAN.md §3.10; port of
// AppStore.recomputeDerivedStats, GroundTeam/Store/AppStore+Sync.swift:22). The server never has them, so every
// device (and the console) derives them from the same activities and gets the same numbers.

import { activityEffectiveDate, isVerifiedVisit, type ActivityPayload } from '@/contracts/models';
import { idKey, ms } from './util';

/** Derived per-record values (records `visitCount`, `lastVisitAt`, … are never on the wire). */
export interface RecordStats {
  /** Completed visits. */
  visitCount: number;
  /** Completed visits that are verified (`Activity.isVerified`; `verifiedVisitCount(for:)`). */
  verifiedVisitCount: number;
  /** Activities with status `scheduled` (the "has a scheduled activity" filter). */
  scheduledCount: number;
  /** Effective date of the newest completed visit. */
  lastVisitAt?: Date;
  /** `resultId` of that visit. */
  lastVisitResultId?: string;
  /** Newest effective date over completed activities of any type. */
  lastActivityAt?: Date;
  /** Soonest `scheduledAt` over scheduled activities. */
  nextScheduledAt?: Date;
}

/** Stats keyed by lower-case record id. Records with no activities have no entry (use `statsOf`). */
export type DerivedStats = ReadonlyMap<string, RecordStats>;

/** Stats of a record without activities. */
export const NO_STATS: Readonly<RecordStats> = Object.freeze({ visitCount: 0, verifiedVisitCount: 0, scheduledCount: 0 });

/** `Activity.effectiveDate`: completion, else schedule, else creation time. */
export function effectiveDate(a: Pick<ActivityPayload, 'completedAt' | 'scheduledAt' | 'createdAt'>): Date {
  return new Date(ms(activityEffectiveDate(a)) ?? 0);
}

/** Epoch ms of `effectiveDate` (no Date allocation, for hot loops). */
export function effectiveMs(a: Pick<ActivityPayload, 'completedAt' | 'scheduledAt' | 'createdAt'>): number {
  return ms(activityEffectiveDate(a)) ?? 0;
}

/**
 * `Activity.isVerified`: a visit logged within the radius captured when it was logged (100 m for legacy visits
 * without one). Visits logged from the office carry no distance, so they are never verified.
 */
export function isVerified(a: Pick<ActivityPayload, 'type' | 'distanceFromRecordMeters' | 'verificationRadius'>): boolean {
  return isVerifiedVisit(a);
}

interface Acc {
  visitCount: number;
  verifiedVisitCount: number;
  scheduledCount: number;
  lastVisitMs?: number;
  lastVisitResultId?: string;
  lastActivityMs?: number;
  nextScheduledMs?: number;
}

/**
 * Port of `recomputeDerivedStats` over every record that has activities (or only `recordIds` when given):
 * - `visitCount`: activities with that `recordId`, `type == visit`, `status == completed`;
 * - `lastVisitAt` / `lastVisitResultId`: the newest such visit by effective date (the first one seen wins a tie);
 * - `lastActivityAt`: the newest effective date of completed activities;
 * - `nextScheduledAt`: the soonest `scheduledAt` of `status == scheduled` activities.
 * iOS also keeps a local `lastActivityAt` floor from its own logging; the console has none, so the synced activities
 * alone decide (the same value a freshly installed iPhone computes).
 */
export function recomputeDerivedStats(
  activities: readonly ActivityPayload[],
  recordIds?: Iterable<string>,
): Map<string, RecordStats> {
  const only = recordIds ? new Set(Array.from(recordIds, idKey)) : null;
  const acc = new Map<string, Acc>();
  for (const a of activities) {
    if (!a.recordId) continue;
    const key = idKey(a.recordId);
    if (only && !only.has(key)) continue;
    let s = acc.get(key);
    if (!s) {
      s = { visitCount: 0, verifiedVisitCount: 0, scheduledCount: 0 };
      acc.set(key, s);
    }
    if (a.status === 'completed') {
      const eff = effectiveMs(a);
      if (s.lastActivityMs === undefined || eff > s.lastActivityMs) s.lastActivityMs = eff;
      if (a.type === 'visit') {
        s.visitCount += 1;
        if (isVerifiedVisit(a)) s.verifiedVisitCount += 1;
        if (s.lastVisitMs === undefined || eff > s.lastVisitMs) {
          s.lastVisitMs = eff;
          s.lastVisitResultId = a.resultId;
        }
      }
    } else if (a.status === 'scheduled') {
      s.scheduledCount += 1;
      const at = ms(a.scheduledAt);
      if (at !== undefined && (s.nextScheduledMs === undefined || at < s.nextScheduledMs)) s.nextScheduledMs = at;
    }
  }
  const out = new Map<string, RecordStats>();
  for (const [key, s] of acc) {
    const stats: RecordStats = {
      visitCount: s.visitCount,
      verifiedVisitCount: s.verifiedVisitCount,
      scheduledCount: s.scheduledCount,
    };
    if (s.lastVisitMs !== undefined) stats.lastVisitAt = new Date(s.lastVisitMs);
    if (s.lastVisitResultId !== undefined) stats.lastVisitResultId = s.lastVisitResultId;
    if (s.lastActivityMs !== undefined) stats.lastActivityAt = new Date(s.lastActivityMs);
    if (s.nextScheduledMs !== undefined) stats.nextScheduledAt = new Date(s.nextScheduledMs);
    out.set(key, stats);
  }
  return out;
}

/** A record's stats (zeros when it has no activities, or when `stats` is not computed yet). */
export function statsOf(stats: DerivedStats | null | undefined, recordId: string | null | undefined): RecordStats {
  if (!stats || !recordId) return NO_STATS;
  return stats.get(idKey(recordId)) ?? NO_STATS;
}
